import {cloneDeep} from 'lodash'
import {ModuleFc} from '@/project'
import {updateAll, updateObject} from '@/store/utils'
import {
  Client, ClientsState, ClientAccount,
  ClientsPayload, UpdateClientContactPayload,
  DeleteClientBankAccountPayload,
} from './types'

export {Client, ClientsState, ClientAccount}

const initState: ClientsState = {
  client: {
    id: '',
    name: '',
    bizRegistrationNumber: '',
    ownerName: '',
    businessClass: '',
    businessType: '',
    clientType: '',
    email: '',
    emailSub: '',
    isCorporate: false,
    phoneNumber: '',
    taxRegistrationNumber: '',
    fullAddress: '',
    sales: null,
  },
  clients: [],
  clientAccounts: [],
  nextOffset: 0,
  selectedAccount: {}
}

const module: ModuleFc<ClientsState> = (ctx) => ({
  namespaced: true,
  state: cloneDeep(initState),
  actions: {
    async getClients({commit, rootState, state}, payload: ClientsPayload = {}) {
      const {
        bizId = rootState.biz.id, name, bizNum, sales,
        limit = 10, offset = state.nextOffset} = payload
      const result = await ctx.$axios({
        url: '/clients',
        params: {
          search: name || bizNum,
          bizId,
          limit,
          offset,
          sales
        },
        method: 'get',
      })
      commit('updateAll', {list: result.data.results, offset: result.data.nextOffset})
      return result.data.results
    },
    async getClient({commit, rootState}, payload: {clientId: string}) {
      const {data} = await ctx.$axios({
        url: '/client/$id',
        params: {
          bizId: rootState.biz.id,
          clientId: payload.clientId
        },
        method: 'get',
      })
      commit('update', data)
      return data
    },
    async createClient({rootState}, payload: Client) {
      const {data} = await ctx.$axios({
        url: '/clients',
        params: {
          bizId: rootState.biz.id,
        },
        method: 'post',
        data: payload,
      })

      return data
    },
    async updateClientContact({rootState}, payload: UpdateClientContactPayload) {
      const {data} = await ctx.$axios({
        url: '/clients/$id',
        params: {
          bizId: rootState.biz.id,
          clientId: payload.id
        },
        method: 'patch',
        data: payload,
      })

      return data
    },
    async getClientAccount({commit, rootState}, payload: {bizId, id}) {
      const result = await ctx.$axios({
        url: '/client/$id/bank-accounts',
        method: 'get',
        data: {
          get bizId() {
            return payload.bizId || rootState.biz.id
          },
          id: payload.id,
        },
      })
      commit('updateClientAccounts', result.data.results)
    },
    async createClientAccount({commit, rootState}, payload: {bizId, id, bank, owner, number}) {
      const {bank, owner, number} = payload
      const result = await ctx.$axios({
        url: '/client/$id/bank-accounts',
        method: 'post',
        data: {
          get bizId() {
            return payload.bizId || rootState.biz.id
          },
          id: payload.id,
          bank,
          owner,
          number,
        },
      })
      commit('updateClientAccount', result.data)
    },
    async deleteClientAccount({rootState}, payload: DeleteClientBankAccountPayload) {
      const {bizId = rootState.biz?.id, clientId, bankAccountId} = payload
      await ctx.$axios({
        url: '/client/$id/bank-accounts/$id',
        method: 'delete',
        params: {
          bizId,
          clientId,
          bankAccountId,
        },
      })
    },
  },
  mutations: {
    update(state, payload: Client) {
      updateObject(state.client, payload)
    },
    updateAll(state, payload: {list: Client[], offset}) {
      updateAll(state.clients, payload.list)
      state.nextOffset = payload.offset
    },
    updateClientAccount(state, payload: ClientAccount) {
      state.clientAccounts.unshift(payload)
    },
    updateClientAccounts(state, payload: ClientAccount[]) {
      state.clientAccounts = payload
    },
    clear(state) {
      updateObject(state, initState)
    },
    clearAccounts(state) {
      state.clientAccounts = []
    },
    updateSelectedClientAccount(state, payload) {
      state.selectedAccount = payload
    }
  },
  getters: {
    noMoreClient(state) {
      return state.nextOffset === null
    }
  }
})

export default module
